import { useRef, useState, type DragEvent } from 'react'

/**
 * Drag-and-drop (or pick) files and zips. Zips go through the zip endpoint, which
 * recreates their folder structure; everything else goes through single upload. The
 * component only collects files; the caller owns the mutations and the progress.
 */
export function UploadDropzone({
  disabled,
  busy,
  onFiles,
}: {
  disabled: boolean
  busy: boolean
  onFiles: (files: File[]) => void
}) {
  const [over, setOver] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    if (disabled) return
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) onFiles(files)
  }

  const className = ['dropzone', over ? 'dropzone-over' : '', disabled ? 'dropzone-disabled' : '']
    .join(' ')
    .trim()

  return (
    <div
      className={className}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <p className="dropzone-text">
        {disabled
          ? 'You cannot upload here.'
          : busy
            ? 'Uploading…'
            : 'Drop files or a zip here, or'}
      </p>
      {!disabled && (
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => input.current?.click()}
        >
          Choose files
        </button>
      )}
      <input
        ref={input}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          e.target.value = ''
          if (files.length > 0) onFiles(files)
        }}
      />
    </div>
  )
}
